import React, { useState, useEffect } from 'react';
import Papa from 'papaparse';
import { supabase } from '../../lib/supabase';

interface CsvImportProps {
    onContactsUpdate?: () => void;
    onDuplicatesFound?: () => void;
    onPendingContactsFound?: () => void;
}

interface CsvProcessingStats {
    totalRows: number;
    processedRows: number;
    successfulImports: number;
    duplicatesFound: number;
    errors: number;
    message?: string;
}

interface CsvResults {
    success: boolean;
    message: string;
    totalContacts?: number;
    errorCount?: number;
    error?: string;
}

export default function CsvImport({ onContactsUpdate, onDuplicatesFound, onPendingContactsFound }: CsvImportProps) {
    const [csvFile, setCsvFile] = useState<File | null>(null);
    const [showPreview, setShowPreview] = useState(false);
    const [csvUploading, setCsvUploading] = useState(false);
    const [csvResults, setCsvResults] = useState<CsvResults | null>(null);
    const [showCsvResults, setShowCsvResults] = useState(false);
    const [activeJobId, setActiveJobId] = useState<string | null>(null);
    const [csvProcessingStats, setCsvProcessingStats] = useState<CsvProcessingStats>({
        totalRows: 0,
        processedRows: 0,
        successfulImports: 0,
        duplicatesFound: 0,
        errors: 0
    });

    // Check for active import on component mount
    useEffect(() => {
        checkForActiveImport();
    }, []);

    const checkForActiveImport = async () => {
        try {
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) return;

            // Check localStorage for active job
            const storedJobId = localStorage.getItem(`csv-import-job-${user.id}`);
            if (!storedJobId) return;

            console.log(`🔍 Checking for active import job: ${storedJobId}`);

            // Check if job is still active
            const { data: { session } } = await supabase.auth.getSession();
            if (!session) return;

            const response = await fetch(`https://hnxenvvnlekhknvjuagb.supabase.co/functions/v1/csv-import?jobId=${storedJobId}&userId=${user.id}`, {
                headers: {
                    'Authorization': `Bearer ${session.access_token}`,
                }
            });

            if (response.ok) {
                const job = await response.json();

                if (job.status === 'pending' || job.status === 'processing') {
                    console.log(`🔄 Resuming import progress for job: ${storedJobId}`);
                    setActiveJobId(storedJobId);
                    setCsvUploading(true);
                    setCsvProcessingStats({
                        totalRows: job.total_rows,
                        processedRows: job.processed_rows,
                        successfulImports: job.successful_imports,
                        duplicatesFound: job.duplicates_found,
                        errors: job.errors
                    });

                    // Resume polling
                    pollImportProgress(storedJobId, user.id);
                } else {
                    // Job completed or failed, clean up
                    localStorage.removeItem(`csv-import-job-${user.id}`);
                }
            }
        } catch (error) {
            console.error('Error checking for active import:', error);
        }
    };

    const handleCsvFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file && file.type === 'text/csv') {
            setCsvFile(file);
            setShowPreview(true);
            // No client-side parsing - file will be processed server-side
        } else {
            alert('Please select a valid CSV file');
        }
    };

    const handleCsvUpload = async () => {
        if (!csvFile) return;

        setCsvUploading(true);
        setShowPreview(false);

        try {
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) {
                alert('Please log in to import contacts');
                setCsvUploading(false);
                return;
            }

            console.log('🚀 Starting CSV Import with authentication');

            // Parse CSV client-side using Papa Parse
            Papa.parse(csvFile, {
                header: true,
                skipEmptyLines: true,
                complete: async (results) => {
                    try {
                        console.log(`🚀 Starting CSV Import: ${results.data.length} contacts to process`);

                        // Implement chunked processing for large imports
                        const CHUNK_SIZE = 250; // Process 250 contacts per chunk (well below the 600 failure point)
                        const totalContacts = results.data.length;

                        if (totalContacts > CHUNK_SIZE) {
                            console.log(`📦 Large import detected (${totalContacts} contacts). Splitting into chunks of ${CHUNK_SIZE}...`);
                            await processChunkedImport(results.data, user);
                        } else {
                            console.log(`📝 Small import (${totalContacts} contacts). Processing normally...`);
                            await processSingleImport(results.data, user);
                        }

                    } catch (error) {
                        console.error('CSV upload error:', error);
                        setCsvResults({
                            success: false,
                            message: 'Failed to import CSV',
                            error: error instanceof Error ? error.message : 'Unknown error'
                        });
                        setCsvUploading(false);
                        setShowCsvResults(true);
                    }
                },
                error: (error) => {
                    console.error('CSV parsing error:', error);
                    setCsvResults({
                        success: false,
                        message: 'Failed to parse CSV file',
                        error: error.message
                    });
                    setCsvUploading(false);
                    setShowCsvResults(true);
                }
            });

        } catch (error) {
            console.error('CSV upload error:', error);
            setCsvResults({
                success: false,
                message: 'Failed to import CSV',
                error: error instanceof Error ? error.message : 'Unknown error'
            });
            setCsvUploading(false);
            setShowCsvResults(true);
        }
    };

    // Process a single import (for small CSV files)
    const processSingleImport = async (csvData: any[], user: any) => {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) {
            throw new Error('No active session');
        }

        const response = await fetch('https://hnxenvvnlekhknvjuagb.supabase.co/functions/v1/csv-import', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${session.access_token}`,
            },
            body: JSON.stringify({
                csvData: csvData,
                userId: user.id
            })
        });

        const data = await response.json();

        if (response.ok && data.success) {
            localStorage.setItem(`csv-import-job-${user.id}`, data.jobId);
            setActiveJobId(data.jobId);
            pollImportProgress(data.jobId, user.id);
        } else {
            throw new Error(data.error || 'Failed to start import');
        }
    };

    // Process a chunked import (for large CSV files)
    const processChunkedImport = async (csvData: any[], user: any) => {
        const CHUNK_SIZE = 250;
        const totalContacts = csvData.length;
        const totalChunks = Math.ceil(totalContacts / CHUNK_SIZE);

        console.log(`📦 Processing ${totalContacts} contacts in ${totalChunks} chunks of ${CHUNK_SIZE}`);

        // Create parent job ID for tracking
        const parentJobId = `chunked-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        localStorage.setItem(`csv-import-job-${user.id}`, parentJobId);
        setActiveJobId(parentJobId);

        // Initialize chunked progress tracking
        const chunkProgress = {
            totalChunks,
            completedChunks: 0,
            totalContacts,
            processedContacts: 0,
            successfulImports: 0,
            duplicatesFound: 0,
            errors: 0,
            chunkJobs: [] as string[]
        };

        localStorage.setItem(`chunked-progress-${user.id}`, JSON.stringify(chunkProgress));

        // Process chunks sequentially
        for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
            const startIndex = chunkIndex * CHUNK_SIZE;
            const endIndex = Math.min(startIndex + CHUNK_SIZE, totalContacts);
            const chunkData = csvData.slice(startIndex, endIndex);

            console.log(`📦 Processing chunk ${chunkIndex + 1}/${totalChunks} (contacts ${startIndex + 1}-${endIndex})`);

            // Update UI to show which chunk we're processing
            setCsvProcessingStats(prev => ({
                ...prev,
                totalRows: totalContacts,
                processedRows: startIndex,
                message: `Processing chunk ${chunkIndex + 1}/${totalChunks}...`
            }));

            try {
                const chunkJobId = await processChunk(chunkData, user, chunkIndex + 1, totalChunks, parentJobId);
                chunkProgress.chunkJobs.push(chunkJobId);

                // Wait for chunk to complete before starting next one
                await waitForChunkCompletion(chunkJobId, user, chunkProgress, chunkIndex + 1, totalChunks);

            } catch (error) {
                console.error(`❌ Error processing chunk ${chunkIndex + 1}:`, error);
                // Continue with next chunk even if one fails
                chunkProgress.errors += chunkData.length; // Mark all contacts in failed chunk as errors
                updateChunkedProgress(chunkProgress, chunkData.length, 0, 0, chunkData.length);
            }
        }

        console.log('🎉 All chunks completed!');

        // Final cleanup
        localStorage.removeItem(`csv-import-job-${user.id}`);
        localStorage.removeItem(`chunked-progress-${user.id}`);
        setActiveJobId(null);
        setCsvUploading(false);

        setCsvResults({
            success: true,
            message: `Chunked import completed! Processed ${chunkProgress.processedContacts}/${totalContacts} contacts`,
            totalContacts: chunkProgress.successfulImports,
            errorCount: chunkProgress.errors
        });
        setShowCsvResults(true);

        if (chunkProgress.successfulImports > 0 && onContactsUpdate) {
            onContactsUpdate();
        }

        // Trigger pending contact review if any contacts need review
        if (chunkProgress.duplicatesFound > 0 && onPendingContactsFound) {
            onPendingContactsFound();
        }
    };

    // Process a single chunk
    const processChunk = async (chunkData: any[], user: any, chunkNumber: number, totalChunks: number, parentJobId: string): Promise<string> => {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) {
            throw new Error('No active session');
        }

        const response = await fetch('https://hnxenvvnlekhknvjuagb.supabase.co/functions/v1/csv-import', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${session.access_token}`,
            },
            body: JSON.stringify({
                csvData: chunkData,
                userId: user.id,
                isChunk: true,
                chunkNumber,
                totalChunks,
                parentJobId
            })
        });

        const data = await response.json();

        if (response.ok && data.success) {
            return data.jobId;
        } else {
            throw new Error(data.error || `Failed to start chunk ${chunkNumber}`);
        }
    };

    // Wait for a chunk to complete processing
    const waitForChunkCompletion = async (chunkJobId: string, user: any, chunkProgress: any, chunkNumber: number, totalChunks: number): Promise<void> => {
        return new Promise((resolve, reject) => {
            const pollInterval = setInterval(async () => {
                try {
                    const { data: { session } } = await supabase.auth.getSession();
                    if (!session) {
                        clearInterval(pollInterval);
                        reject(new Error('No active session'));
                        return;
                    }

                    const response = await fetch(`https://hnxenvvnlekhknvjuagb.supabase.co/functions/v1/csv-import?jobId=${chunkJobId}&userId=${user.id}`, {
                        headers: {
                            'Authorization': `Bearer ${session.access_token}`,
                        }
                    });

                    if (response.ok) {
                        const job = await response.json();

                        // Update local chunk progress (not the main progress tracking yet)
                        if (job.status === 'completed') {
                            console.log(`✅ Chunk ${chunkNumber}/${totalChunks} completed: ${job.successful_imports} successful, ${job.duplicates_found} duplicates, ${job.errors} errors`);

                            // Update overall progress
                            chunkProgress.completedChunks++;
                            chunkProgress.processedContacts += job.processed_rows;
                            chunkProgress.successfulImports += job.successful_imports;
                            chunkProgress.duplicatesFound += job.duplicates_found;
                            chunkProgress.errors += job.errors;

                            updateChunkedProgress(chunkProgress);

                            clearInterval(pollInterval);
                            resolve();
                        } else if (job.status === 'failed') {
                            console.error(`❌ Chunk ${chunkNumber}/${totalChunks} failed`);
                            clearInterval(pollInterval);
                            reject(new Error(`Chunk ${chunkNumber} failed`));
                        }
                        // Continue polling if still processing
                    } else {
                        console.error(`Error polling chunk ${chunkNumber}:`, response.statusText);
                    }
                } catch (error) {
                    console.error(`Error polling chunk ${chunkNumber}:`, error);
                }
            }, 2000); // Poll every 2 seconds

            // Set a timeout for chunk completion (5 minutes per chunk)
            setTimeout(() => {
                clearInterval(pollInterval);
                reject(new Error(`Chunk ${chunkNumber} timed out`));
            }, 5 * 60 * 1000);
        });
    };

    // Update chunked progress and UI
    const updateChunkedProgress = (chunkProgress: any) => {
        // Update UI
        setCsvProcessingStats({
            totalRows: chunkProgress.totalContacts,
            processedRows: chunkProgress.processedContacts,
            successfulImports: chunkProgress.successfulImports,
            duplicatesFound: chunkProgress.duplicatesFound,
            errors: chunkProgress.errors
        });
    };

    const pollImportProgress = async (jobId: string, userId: string) => {
        let retryCount = 0;
        const maxRetries = 3;
        const baseDelay = 1000; // Start with 1 second delay

        const pollInterval = setInterval(async () => {
            try {
                // Get session for authentication
                const { data: { session } } = await supabase.auth.getSession();
                if (!session) {
                    throw new Error('No active session');
                }

                // Add timeout to fetch call
                const controller = new AbortController();
                const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout

                const response = await fetch(`https://hnxenvvnlekhknvjuagb.supabase.co/functions/v1/csv-import?jobId=${jobId}&userId=${userId}`, {
                    headers: {
                        'Authorization': `Bearer ${session.access_token}`,
                    },
                    signal: controller.signal
                });

                clearTimeout(timeoutId);

                if (!response.ok) {
                    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
                }

                const job = await response.json();

                // Reset retry count on successful request
                retryCount = 0;

                setCsvProcessingStats({
                    totalRows: job.total_rows,
                    processedRows: job.processed_rows,
                    successfulImports: job.successful_imports,
                    duplicatesFound: job.duplicates_found,
                    errors: job.errors
                });

                if (job.status === 'completed' || job.status === 'failed') {
                    clearInterval(pollInterval);
                    setCsvUploading(false);

                    // Clean up localStorage
                    const { data: { user } } = await supabase.auth.getUser();
                    if (user) {
                        localStorage.removeItem(`csv-import-job-${user.id}`);
                    }
                    setActiveJobId(null);

                    setCsvResults({
                        success: job.status === 'completed',
                        message: job.status === 'completed'
                            ? `Successfully processed ${job.successful_imports} contacts`
                            : 'Import failed',
                        totalContacts: job.successful_imports,
                        errorCount: job.errors
                    });
                    setShowCsvResults(true);

                    if (job.successful_imports > 0 && onContactsUpdate) {
                        onContactsUpdate();
                    }

                    // Trigger pending contact review if contacts need review
                    if (job.duplicates_found > 0 && onPendingContactsFound) {
                        onPendingContactsFound();
                    }

                    // Trigger duplicate detection if duplicates were found (kept for backwards compatibility)
                    if (job.duplicates_found > 0 && onDuplicatesFound) {
                        onDuplicatesFound();
                    }
                } else if (job.status === 'paused_timeout') {
                    // Job was paused due to timeout - it will auto-resume on next poll
                    console.log('🔄 Job paused due to timeout, will auto-resume...');
                    // Continue polling - the server will resume processing automatically
                    if (job.duplicates_found > 0 && onPendingContactsFound) {
                        onPendingContactsFound();
                    }
                    if (job.duplicates_found > 0 && onDuplicatesFound) {
                        onDuplicatesFound();
                    }
                }
            } catch (error) {
                console.error('Error polling import progress:', error);

                retryCount++;

                // If it's a network error and we haven't exceeded max retries
                if (retryCount <= maxRetries && (
                    error.name === 'AbortError' ||
                    error.message?.includes('Failed to fetch') ||
                    error.message?.includes('Network') ||
                    error.message?.includes('upstream connect error')
                )) {
                    console.log(`Retrying in ${baseDelay * retryCount}ms (attempt ${retryCount}/${maxRetries})`);
                    // Don't clear interval, just continue polling with exponential backoff
                    setTimeout(() => {
                        // The next poll will happen at the regular interval
                    }, baseDelay * retryCount);
                    return;
                }

                // If we've exceeded retries or it's not a network error, show error
                clearInterval(pollInterval);
                setCsvUploading(false);
                setActiveJobId(null);

                setCsvResults({
                    success: false,
                    message: 'Failed to check import progress. The import may still be running in the background.',
                    error: error instanceof Error ? error.message : 'Unknown error'
                });
                setShowCsvResults(true);
            }
        }, 2000); // Poll every 2 seconds
    };

    const resetCsvUpload = async () => {
        // Clean up localStorage
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
            localStorage.removeItem(`csv-import-job-${user.id}`);
        }

        setCsvFile(null);
        setShowPreview(false);
        setCsvResults(null);
        setShowCsvResults(false);
        setCsvUploading(false);
        setActiveJobId(null);
    };

    const cancelImport = async () => {
        try {
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) return;

            // Clear localStorage
            localStorage.removeItem(`csv-import-job-${user.id}`);

            // Reset UI state
            setCsvUploading(false);
            setActiveJobId(null);
            setCsvFile(null);
            setShowPreview(false);
            setCsvResults(null);
            setShowCsvResults(false);
            setCsvProcessingStats({
                totalRows: 0,
                processedRows: 0,
                successfulImports: 0,
                duplicatesFound: 0,
                errors: 0
            });

            console.log('🗑️ Import cancelled and cleared');

            // Show success message
            setCsvResults({
                success: true,
                message: 'Import cancelled successfully. You can now start a new import.'
            });
            setShowCsvResults(true);

        } catch (error) {
            console.error('Error cancelling import:', error);
            alert('Error cancelling import. Please refresh the page and try again.');
        }
    };

    return (
        <div className="space-y-6">
            <h3 className="text-lg font-semibold text-white">CSV Import</h3>

            {/* CSV File Selection */}
            <div className="space-y-4">
                <div>
                    <label htmlFor="csv-file" className="block text-sm font-medium text-gray-300 mb-2">
                        Select CSV File
                    </label>
                    <input
                        id="csv-file"
                        type="file"
                        accept=".csv"
                        onChange={handleCsvFileSelect}
                        className="block w-full text-sm text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-blue-600 file:text-white hover:file:bg-blue-700"
                    />
                </div>

                {/* CSV Preview */}
                {/* CSV Preview */}
                {showPreview && csvFile && !csvUploading && (
                    <div className="bg-gray-800 p-4 rounded-lg">
                        <h4 className="text-md font-medium text-white mb-2">File Selected: {csvFile.name}</h4>
                        <p className="text-sm text-gray-300 mb-4">
                            File will be parsed client-side using Papa Parse and then sent to the server for processing.
                        </p>
                        <div className="text-xs text-blue-400">
                            💡 The CSV will be parsed to extract contact information including names, emails, companies, and investment data.
                        </div>
                    </div>
                )}

                {/* Upload Button */}
                {csvFile && (
                    <div className="flex gap-3">
                        <button
                            onClick={handleCsvUpload}
                            disabled={csvUploading}
                            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {csvUploading ? 'Processing...' : 'Import CSV'}
                        </button>
                        <button
                            onClick={resetCsvUpload}
                            disabled={csvUploading}
                            className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 disabled:opacity-50"
                        >
                            Cancel
                        </button>
                    </div>
                )}

                {/* Processing Stats */}
                {csvUploading && (
                    <div className="bg-gray-800 p-4 rounded-lg border border-blue-500">
                        <div className="flex items-center justify-between mb-3">
                            <h4 className="text-md font-medium text-white">Import Progress (Server-side)</h4>
                            <div className="flex items-center gap-2">
                                {activeJobId && (
                                    <span className="text-xs bg-blue-600 text-white px-2 py-1 rounded">
                                        Job ID: {activeJobId.slice(-8)}
                                    </span>
                                )}
                                <button
                                    onClick={cancelImport}
                                    className="text-xs bg-red-600 text-white px-2 py-1 rounded hover:bg-red-700"
                                    title="Cancel this import and clear the job"
                                >
                                    Cancel Import
                                </button>
                            </div>
                        </div>
                        <div className="space-y-2 text-sm text-gray-300">
                            <div>Processing {csvProcessingStats.processedRows}/{csvProcessingStats.totalRows} contacts</div>
                            {csvProcessingStats.message && (
                                <div className="text-xs text-blue-300">📦 {csvProcessingStats.message}</div>
                            )}
                            <div>✅ Updated Existing: {csvProcessingStats.successfulImports}</div>
                            <div>❓ Need Review: {csvProcessingStats.duplicatesFound}</div>
                            <div>❌ Errors: {csvProcessingStats.errors}</div>
                            <div className="text-xs text-blue-400 mt-2">
                                💡 CSV import only UPDATES existing contacts. Unmatched contacts will be flagged for duplicate detection review.
                                <br />
                                📦 Large imports (250+ contacts) are automatically split into smaller chunks to prevent timeouts.
                            </div>
                        </div>
                        <div className="w-full bg-gray-700 rounded-full h-2 mt-3">
                            <div
                                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                                style={{
                                    width: `${csvProcessingStats.totalRows > 0 ? (csvProcessingStats.processedRows / csvProcessingStats.totalRows) * 100 : 0}%`
                                }}
                            ></div>
                        </div>
                        <div className="text-center text-xs text-gray-400 mt-1">
                            {csvProcessingStats.totalRows > 0 ? Math.round((csvProcessingStats.processedRows / csvProcessingStats.totalRows) * 100) : 0}% Complete
                        </div>
                    </div>
                )}

                {/* Results */}
                {showCsvResults && csvResults && (
                    <div className={`p-4 rounded-lg ${csvResults.success ? 'bg-green-900' : 'bg-red-900'}`}>
                        <h4 className="text-md font-medium text-white mb-2">
                            Import {csvResults.success ? 'Completed' : 'Failed'}
                        </h4>
                        <p className="text-sm text-gray-200">{csvResults.message}</p>
                        {csvResults.totalContacts && (
                            <p className="text-sm text-gray-200 mt-1">
                                Total contacts updated: {csvResults.totalContacts}
                            </p>
                        )}
                        {csvResults.errorCount && csvResults.errorCount > 0 && (
                            <p className="text-sm text-gray-200 mt-1">
                                Errors encountered: {csvResults.errorCount}
                            </p>
                        )}
                        {csvProcessingStats.duplicatesFound > 0 && (
                            <div className="mt-3 p-3 bg-yellow-900/30 border border-yellow-600 rounded">
                                <p className="text-sm text-yellow-300">
                                    ❓ {csvProcessingStats.duplicatesFound} contact(s) need review (no existing match found).
                                </p>
                                <p className="text-xs text-yellow-400 mt-1">
                                    Use the Duplicate Detection tool to review these contacts and decide whether to create new contacts or match with existing ones.
                                </p>
                            </div>
                        )}
                        {csvResults.error && (
                            <p className="text-sm text-red-300 mt-1">Error: {csvResults.error}</p>
                        )}
                        <button
                            onClick={() => setShowCsvResults(false)}
                            className="mt-3 px-3 py-1 bg-gray-600 text-white rounded text-sm hover:bg-gray-700"
                        >
                            Close
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}
